<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm'
import type { DefaultMessageDto, DefaultMessageType } from '@swipe4work/models-orval'
import { DefaultMessageType as DefaultMessageTypeEnum } from '@swipe4work/models-orval'
import DefaultMessagesContent from './components/DefaultMessagesContent.vue'

definePage({
  name: '/settings/DefaultMessages',
  path: '/instellingen/standaard-berichten',
  meta: {
    layout: 'false',
  },
})

const { t } = useI18n()

const confirm = useConfirm()
const companyStore = useCompanyStore()

companyStore.fetchCompany()

const { company } = storeToRefs(companyStore)

const messageTypeOptions = Object.keys(DefaultMessageTypeEnum)
  .filter(key => key !== DefaultMessageTypeEnum.CloseMatch)
  .map(key => ({
    label: t(`settings.defaultMessages.types.${key}.label`),
    value: key as DefaultMessageType,
    description: t(`settings.defaultMessages.types.${key}.description`),
  }))

const dialogVisible = ref(false)
const editingMessage = ref<DefaultMessageDto | null>(null)
const isSubmitting = ref(false)

function openAddDialog() {
  editingMessage.value = null
  dialogVisible.value = true
}

function openEditDialog(defaultMessage: DefaultMessageDto) {
  editingMessage.value = defaultMessage
  dialogVisible.value = true
}

async function onSubmit(entity: {
  id?: number,
  messageType: DefaultMessageType,
  title: string,
  body: string 
}) {
  isSubmitting.value = true
  try {
    const success = await companyStore.addOrUpdateDefaultMessage(entity)
    if (success) {
      dialogVisible.value = false
      await companyStore.fetchCompany()
    }
  } finally {
    isSubmitting.value = false
  }
}

function deleteDefaultMessage(defaultMessageId: number) {
  confirm.require({
    header: t('confirm'),
    message: t('settingsPage.confirmDeleteDefaultMessage'),
    acceptLabel: t('yes'),
    rejectLabel: t('no'),
    accept: async () => {
      await companyStore.deleteDefaultMessage(defaultMessageId)
      await companyStore.fetchCompany()
    },
  })
}
</script>

<template>
  <DefaultMessagesContent
    v-model:visible="dialogVisible"
    :default-messages="company?.defaultMessages"
    :message-type-options="messageTypeOptions"
    :company-name="company?.name"
    :editing-message="editingMessage"
    :is-submitting="isSubmitting"
    @add="openAddDialog"
    @edit="openEditDialog"
    @delete="deleteDefaultMessage"
    @submit="onSubmit"
  />
</template>
